"use client"

import { submitContactMessage } from "@lib/data/contact"
import { Button, Input, Label, Text } from "@modules/common/components/ui"
import { FormEvent, useState } from "react"

export default function ContactForm() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [orderNumber, setOrderNumber] = useState("")
  const [message, setMessage] = useState("")
  // Honeypot: invisible to humans, filled by dumb bots. A non-empty value
  // short-circuits to the success state without ever hitting the backend.
  const [website, setWebsite] = useState("")
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle")

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (status === "submitting" || !email || !message) {
      return
    }

    if (website) {
      setStatus("success")
      return
    }

    setStatus("submitting")

    const sent = await submitContactMessage({
      name,
      email,
      order_number: orderNumber || undefined,
      message,
    })

    setStatus(sent ? "success" : "error")
  }

  if (status === "success") {
    return (
      <div
        className="rounded-md border border-green-200 bg-green-50 p-4"
        data-testid="contact-form-success"
      >
        <Text className="text-sm font-medium text-green-800" role="status">
          Message sent &mdash; we&rsquo;ll get back to you within one business
          day.
        </Text>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full max-w-xl flex-col gap-y-4"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 gap-4 xsmall:grid-cols-2">
        <Input
          label="Name"
          name="name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="contact-name"
        />
        <Input
          label="Email"
          name="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="contact-email"
        />
      </div>
      <Input
        label="Order # (optional)"
        name="order_number"
        value={orderNumber}
        onChange={(e) => setOrderNumber(e.target.value)}
        data-testid="contact-order-number"
      />
      <div className="flex flex-col gap-1">
        <Label htmlFor="contact-message">Message</Label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          maxLength={5000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
          placeholder="Tell us about your project, your vehicle, or your order"
          data-testid="contact-message"
        />
      </div>
      <div aria-hidden="true" className="hidden">
        <label htmlFor="contact-website">Website</label>
        <input
          id="contact-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      <Button
        type="submit"
        variant="primary"
        className="self-start px-6"
        isLoading={status === "submitting"}
        data-testid="contact-submit"
      >
        Send message
      </Button>
      {status === "error" && (
        <Text
          role="alert"
          className="text-sm text-red-600"
          data-testid="contact-form-error"
        >
          Couldn&rsquo;t send your message &mdash; please try again, or email
          us directly.
        </Text>
      )}
    </form>
  )
}
